// src/services/sectionService.js

// Use the same logic as bookingService
const API_BASE_URL = process.env.NODE_ENV === 'development' ?
    '/api' // In development, proxy to local PHP server
    :
    'https://nlngraclub.com/nlnapi'; // In production, direct to nlnapi

const getToken = () => localStorage.getItem('nlngra_token');

const handleResponse = async(response) => {
    const text = await response.text();
    console.log('Raw response preview:', text.substring(0, 500));

    // Remove PHP warnings and notices
    let cleanResponse = text;
    cleanResponse = cleanResponse.replace(/<br\s*\/?>\s*<b>Warning<\/b>.*?(?=<br|<\\?|\{)/gis, '');
    cleanResponse = cleanResponse.replace(/<b>Warning<\/b>.*?<br\s*\/?>/gis, '');
    cleanResponse = cleanResponse.replace(/<b>Notice<\/b>.*?<br\s*\/?>/gis, '');
    cleanResponse = cleanResponse.replace(/<br\s*\/?>\s*<b>.*?<\/b>.*?\n/g, '');
    cleanResponse = cleanResponse.replace(/Cannot modify header information.*?\n/g, '');
    cleanResponse = cleanResponse.replace(/<[^>]*>/g, '');
    cleanResponse = cleanResponse.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

    const lastBraceIndex = cleanResponse.lastIndexOf('}');
    const lastBracketIndex = cleanResponse.lastIndexOf(']');
    const lastValidIndex = Math.max(lastBraceIndex, lastBracketIndex);

    if (lastValidIndex > -1) {
        cleanResponse = cleanResponse.substring(0, lastValidIndex + 1);
    }

    const firstBraceIndex = cleanResponse.indexOf('{');
    const firstBracketIndex = cleanResponse.indexOf('[');
    let firstValidIndex = -1;

    if (firstBraceIndex !== -1 && (firstBracketIndex === -1 || firstBraceIndex < firstBracketIndex)) {
        firstValidIndex = firstBraceIndex;
    } else if (firstBracketIndex !== -1) {
        firstValidIndex = firstBracketIndex;
    }

    if (firstValidIndex > -1) {
        cleanResponse = cleanResponse.substring(firstValidIndex);
    }

    try {
        const parsed = JSON.parse(cleanResponse);
        console.log('✅ JSON parsed successfully');
        return parsed;
    } catch (e) {
        console.error('❌ JSON parse error:', e);
        throw new Error('Invalid JSON format: ' + e.message);
    }
};

export const sectionService = {
    // PUBLIC ENDPOINT - Get all sections for regular users
    getAllSectionsPublic: async() => {
        try {
            console.log('📡 Fetching sections from public endpoint...');
            console.log('📡 API_BASE_URL:', API_BASE_URL);

            const response = await fetch(`${API_BASE_URL}/sections.php?action=public_list&_=${Date.now()}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            console.log('📡 Response status:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await handleResponse(response);
            console.log('✅ Public sections loaded:', data.success ? 'Success' : 'Failed');
            if (data.sections) {
                console.log('📊 Sections count:', data.sections.length);
            }
            return data;
        } catch (error) {
            console.error('❌ Error fetching public sections:', error);
            return { success: false, sections: [], error: error.message };
        }
    },

    // ADMIN ONLY - Get all sections with counts
    getAllSectionsWithCounts: async() => {
        try {
            console.log('📡 Fetching sections with counts (admin only)...');
            const response = await fetch(`${API_BASE_URL}/sections.php?action=get_all_sections_with_counts&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.status === 403) {
                console.warn('⚠️ Admin access required for section counts');
                return { success: false, sections: [], error: 'Admin access required' };
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await handleResponse(response);
            console.log('✅ Sections with counts loaded:', data);
            return data;
        } catch (error) {
            console.error('❌ Error fetching sections with counts:', error);
            return { success: false, sections: [], error: error.message };
        }
    },

    // Get all sections (simple list)
    getAllSections: async() => {
        try {
            console.log('📡 Fetching sections...');
            const response = await fetch(`${API_BASE_URL}/sections.php?action=list&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('✅ Sections loaded:', data);
            return data;
        } catch (error) {
            console.error('❌ Error fetching sections:', error);
            throw error;
        }
    },

    // Submit join request
    joinSection: async(data) => {
        try {
            console.log('📡 Submitting join request...');
            const response = await fetch(`${API_BASE_URL}/sections.php?action=join`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                body: JSON.stringify(data),
                credentials: 'include'
            });
            return await handleResponse(response);
        } catch (error) {
            console.error('❌ Join section error:', error);
            throw error;
        }
    },

    // Get user's memberships
    getMyMemberships: async() => {
        try {
            console.log('📡 Fetching my memberships...');
            const response = await fetch(`${API_BASE_URL}/sections.php?action=my_memberships&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('📡 My memberships response:', data);
            return data;
        } catch (error) {
            console.error('❌ Get memberships error:', error);
            throw error;
        }
    },

    // Cancel a section membership request
    cancelMembership: async(membershipId) => {
        try {
            console.log('📡 Cancelling membership request for ID:', membershipId);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ membership_id: membershipId }),
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('📡 Cancel response:', data);
            return data;
        } catch (error) {
            console.error('❌ Cancel membership error:', error);
            throw error;
        }
    },

    // Get pending section requests (admin only)
    getPendingRequests: async(status = 'pending', limit = 100, offset = 0) => {
        try {
            console.log('📡 Fetching pending section requests...');
            const response = await fetch(`${API_BASE_URL}/sections.php?action=admin_list&status=${status}&limit=${limit}&offset=${offset}&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('📡 Pending requests response:', data);
            return data;
        } catch (error) {
            console.error('❌ Get pending requests error:', error);
            throw error;
        }
    },

    // Approve/reject section request (admin only)
    updateRequestStatus: async(requestId, status, reason = null) => {
        try {
            console.log(`📡 Updating request ${requestId} to status: ${status}`);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=admin_approve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    request_id: requestId,
                    status: status,
                    reason: reason
                }),
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('📡 Update request response:', data);
            return data;
        } catch (error) {
            console.error('❌ Update request error:', error);
            throw error;
        }
    },

    // Get jersey sizes from lookup table
    getJerseySizes: async() => {
        try {
            console.log('📡 Fetching jersey sizes...');
            const response = await fetch(`${API_BASE_URL}/lookup.php?action=jersey_sizes&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('✅ Jersey sizes loaded:', data);
            return data;
        } catch (error) {
            console.error('❌ Error fetching jersey sizes:', error);
            return { success: true, sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'] };
        }
    },

    // Check if user is already a member of a section
    checkMembershipStatus: async(sectionId) => {
        try {
            const memberships = await sectionService.getMyMemberships();
            if (memberships.success && memberships.memberships) {
                const existing = memberships.memberships.find(function(m) {
                    return parseInt(m.section_id) === parseInt(sectionId);
                });
                return existing ? existing.status : null;
            }
            return null;
        } catch (error) {
            console.error('❌ Check membership error:', error);
            return null;
        }
    },

    // Get available sections for staff assignment
    getAvailableSectionsForStaff: async(memberId = null) => {
        try {
            const url = memberId ?
                `${API_BASE_URL}/sections.php?action=get_available_sections&member_id=${memberId}&_=${Date.now()}` :
                `${API_BASE_URL}/sections.php?action=get_available_sections&_=${Date.now()}`;

            console.log('📡 Fetching available sections for staff assignment...');
            const response = await fetch(url, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('✅ Available sections loaded:', data);
            return data;
        } catch (error) {
            console.error('❌ Error fetching available sections:', error);
            throw error;
        }
    },

    // Update staff positions
    updateStaffPositions: async(assignments, memberId = null) => {
        try {
            console.log('📡 Updating staff positions...', assignments);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=update_staff_positions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ assignments, member_id: memberId }),
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('✅ Staff positions updated:', data);
            return data;
        } catch (error) {
            console.error('❌ Error updating staff positions:', error);
            throw error;
        }
    },

    // Get staff members for a specific section
    getSectionStaff: async(sectionId) => {
        try {
            console.log(`📡 Fetching staff for section ${sectionId}...`);
            const response = await fetch(`${API_BASE_URL}/sections.php?action=get_section_staff&section_id=${sectionId}&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('✅ Section staff loaded:', data);
            return data;
        } catch (error) {
            console.error('❌ Error fetching section staff:', error);
            throw error;
        }
    },

    // Get current user's staff assignments
    getMyStaffAssignments: async() => {
        try {
            console.log('📡 Fetching my staff assignments...');
            const response = await fetch(`${API_BASE_URL}/sections.php?action=get_available_sections&_=${Date.now()}`, {
                headers: {
                    'Authorization': 'Bearer ' + getToken(),
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await handleResponse(response);
            console.log('✅ My staff assignments loaded:', data);
            return data;
        } catch (error) {
            console.error('❌ Error fetching staff assignments:', error);
            throw error;
        }
    }
};